import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const leLavandouEn: ChauffeurPage = {
  slug: "le-lavandou",
  title: "Private chauffeur & VTC in Le Lavandou",
  subtitle:
    "Private chauffeur in Le Lavandou: twelve beaches, the ferry pier for the Hyères islands and transfers to Toulon-Hyères and Nice.",
  keywords: [
    "private chauffeur le lavandou",
    "vtc le lavandou",
    "le lavandou airport transfer",
    "chauffeur lavandou ferry pier",
  ],
  hero: {
    title: "Private chauffeur & VTC in Le Lavandou",
    subtitle:
      "Twelve beaches, the seafront and the island ferry pier: a chauffeur for the whole stay, around the clock.",
    keywords: [
      "VTC Le Lavandou",
      "seafront chauffeur",
      "Porquerolles ferry pier",
      "Toulon-Hyères airport transfer",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur & VTC in Le Lavandou",
    badges: [
      "Toulon-Hyères airport ~35 min",
      "Island ferry pier",
      "FR/EN chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Le Lavandou",
    items: [
      {
        title: "Nearest airport",
        description:
          "Toulon-Hyères at around 35 min; Nice Côte d'Azur is still an option at close to 1h40 for international flights.",
      },
      {
        title: "Twelve beaches to reach",
        description:
          "From Saint-Clair to Pramousquier, drop-off as close as possible to the sand and pick-up at the agreed time.",
      },
      {
        title: "Departure for the islands",
        description:
          "Link to the pier for Porquerolles, Port-Cros and Le Levant, with luggage carried to the quay.",
      },
      {
        title: "Rated 5.0/5",
        description:
          "Punctual, discreet chauffeurs based on the French Riviera, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Our chauffeur services in Le Lavandou",
    services: [
      {
        title: "Toulon-Hyères airport transfer",
        text: "Around 35 min of driving, flight tracking included, to and from Toulon-Hyères airport.",
      },
      {
        title: "Nice airport transfer",
        text: "For long-haul flights, a link to Nice Côte d'Azur in close to 1h40, premium van on request.",
      },
      {
        title: "Ferry pier & beach shuttles",
        text: "Drop-off at the port for the Hyères island ferries and back-and-forth runs to the twelve beaches.",
      },
      {
        title: "Toulon TGV station",
        text: "Transfer to Toulon station for TGV trains to Paris, with a margin for the coastal traffic.",
      },
      {
        title: "Hourly hire & long distance",
        text: "A chauffeur by the day across the Var, or runs to Saint-Tropez, Cannes, Nice and Italy.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Le Lavandou seafront",
        description: "Promenade, quays and the heart of the resort.",
      },
      {
        name: "Island ferry pier",
        description: "Departures for Porquerolles, Port-Cros and Le Levant.",
      },
      {
        name: "Saint-Clair beach",
        description: "Large beach to the east of the resort.",
      },
      {
        name: "Cavalière & Pramousquier",
        description: "Corniche beaches heading towards Cavalaire.",
      },
      {
        name: "Toulon-Hyères airport",
        description: "The nearest airport, around 35 min away.",
      },
      {
        name: "Bormes-les-Mimosas",
        description: "Neighbouring flowered village, a few minutes away.",
      },
    ],
  },
  generic: {
    heading: "Le Lavandou, twelve beaches and the open sea",
    subheading:
      "A resort stretched along the shore, where each beach has its own entrance and its own car park.",
    paragraphs: [
      "Le Lavandou lines up twelve beaches, from Saint-Clair to Pramousquier, separated by a corniche and sometimes narrow accesses. A private chauffeur drops you as close as possible to the one you have in mind and returns at the agreed time, sparing you the loops for a parking space that thins out as soon as the fine weather arrives.",
      "The port houses the pier for the Hyères islands: we drop you there with your luggage for the Porquerolles, Port-Cros and Le Levant ferries, and collect you on the way back. On the airport side, Toulon-Hyères is the nearest at around 35 minutes, while Nice Côte d'Azur is reached in close to 1h40 for international flights; Toulon's TGV station rounds out the access to Paris.",
      "For the rest of the stay, hourly hire entrusts the same chauffeur to your days: beach runs, dinners in Bormes or the gulf, outings to Saint-Tropez, with fixed fares announced in advance and an instant quote.",
    ],
    bullets: [
      "Drop-off at each beach",
      "Island ferry link",
      "Fixed fares & instant quote",
    ],
  },
  process: {
    heading: "Book a chauffeur in Le Lavandou",
    steps: [
      "Tell us your flight, your island ferry or your address in Le Lavandou.",
      "We advise the vehicle to suit the luggage, the group and the distance.",
      "The chauffeur tracks the flight or the boat time and sets the departure.",
      "Direct transfer or hourly hire for the beaches and the outings.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Le Lavandou"),
    {
      q: "Which airport is closest to Le Lavandou?",
      a: "Toulon-Hyères airport, around 35 minutes away, is the nearest. For international flights, Nice Côte d'Azur is close to 1h40 by the A57.",
    },
    {
      q: "Do you drop off at the pier for Porquerolles?",
      a: "Yes. We take you to the port of Le Lavandou for the Hyères island ferries — Porquerolles, Port-Cros, Le Levant — and collect you on the way back, luggage included.",
    },
    {
      q: "Do you handle transfers to Toulon TGV station?",
      a: "Yes, Toulon station links Paris by TGV; we schedule the departure to your train time and anticipate the coastal traffic in season.",
    },
    {
      q: "How far in advance should I book?",
      a: "In summer, book as early as possible: the beaches and the ferry pier are very busy. Last-minute requests are handled subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, they are bilingual French/English and used to an international clientele of holidaymakers and boaters.",
    },
    {
      q: "Which vehicle for a family with luggage?",
      a: "A premium 7-seater van suits families and luggage; E-Class or S-Class sedans are ideal for two to three travellers.",
    },
    {
      q: "Do you cover long distances from Le Lavandou?",
      a: "Yes: Saint-Tropez, Cannes, Nice, Monaco, Marseille and Italy, as well as runs from Cannes or Nice to Le Lavandou, in premium comfort.",
    },
  ],
  relatedLinks: [
    {
      label: "Saint-Tropez chauffeur rates",
      href: "/en/chauffeur/saint-tropez",
    },
    {
      label: "Car service on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Chauffeur service Nice", href: "/en/chauffeur/nice" },
    {
      label: "Rent a car Le Lavandou",
      href: "/en/location/car-rental-le-lavandou",
    },
  ],
  cta: {
    title: "Your private chauffeur in Le Lavandou",
    text: "Airport transfer, island ferry pier or a tour of the beaches: book your chauffeur, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book / request a quote",
  },
  seo: {
    title: "Car service Le Lavandou — private chauffeur & transfers",
    description:
      "Private chauffeur in Le Lavandou: transfer to Toulon-Hyères (~35 min) and Nice airports, Toulon TGV station, twelve beaches and the Hyères ferry pier.",
    canonical: "/en/chauffeur/le-lavandou",
  },
};

export default leLavandouEn;
